import React, { useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import withAuth from '../../../utils/hocs/withAuth';
import withSidebar from '../../../utils/hocs/withSidebar';
import SCOPE from '../../../utils/gate/scope';
import withPermission from '../../../utils/hocs/withPermission';
import useNotification from '../../../utils/hooks/useNotification';
import buildTabs from '../../../utils/tools/buildTabs';
import accommodationCreationSchema from '../../../validations/products/accommodation/creationSchema';
import useForm from '../../../utils/hooks/useForm';

// Services
import useAccommodationService from '../../../services/accommodationService';

// Icons
import { ReactComponent as FileIcon } from '../../../assets/icons/file.svg';
import { ReactComponent as PictureIcon } from '../../../assets/icons/picture.svg';
import { ReactComponent as ToolsIcon } from '../../../assets/icons/tools.svg';

// Components
import PageWrapper from '../../../components/layout/PageWrapper/PageWrapper';
import General from '../../../components/views/establishment/Products/Accommodations/Sections/General/General';
import Features from '../../../components/views/establishment/Products/Accommodations/Sections/Features/Features';
import LayoutWithTabs from '../../../components/layout/LayoutWithTabs/LayoutWithTabs';
import Medias from '../../../components/views/establishment/Products/Accommodations/Sections/Medias/Medias';
import SkeletonLoader from '../../../components/molecules/SkeletonLoader/SkeletonLoader';
import Button from '../../../components/atoms/Button/Button';
import Translations from '../../../components/views/establishment/Products/Common/Translations/Translations';

function AccommodationCreation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  const [initialValues] = useState({
    name: '',
    type: null,
    features: [],
    capacity: '',
    size: '',
    spec: null,
    checkInFromHour: null,
    checkOutUntilHour: null,
    isBreakfastIncluded: false,
    mainImageMedia: null,
    sliderImageMedias: null,

    // english content
    title: '',
    description: '',
  });

  const { postAccommodation } = useAccommodationService();

  const { isLoading: isPostAccommodationLoading, mutate: postAccommodationMutate } = useMutation(
    (values) => postAccommodation(values),
    {
      onSuccess: () => {
        navigate('/products/accommodations', {
          state: 'accommodations.creation.form.success',
        });
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'accommodations.creation.form.error'),
          color: 'error',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        postAccommodationMutate({
          name: values.name,
          type: values.type?.value,
          features: values.features?.map((feature) => feature.value) || [],
          capacity: values.capacity,
          size: values.size,
          isAnAdultMandatory: values.spec?.value === 'isAnAdultMandatory' || false,
          isForChildrenOnly: values.spec?.value === 'isForChildrenOnly' || false,
          checkInFromHour: values.checkInFromHour?.value,
          checkOutUntilHour: values.checkOutUntilHour?.value,
          isBreakfastIncluded: values.isBreakfastIncluded,
          mainImageMediaId: values.mainImageMedia?.id,
          sliderImageMediaIds: values.sliderImageMedias?.map((media) => media.id),

          // english content
          englishTitle: values.title,
          englishDescription: values.description,
        });
      }
    },
    validationSchema: accommodationCreationSchema,
    validateOnBlur: true,
  });

  const getTabComponent = (TabComponent, props) => (
    <TabComponent
      formik={{
        values: formik.values,
        handleChange: formik.handleChange,
        handleBlur: formik.handleBlur,
        setFieldValue: formik.setFieldValue,
        setFieldTouched: formik.setFieldTouched,
        touched: formik.touched,
        errors: formik.errors,
      }}
      {...props}
    />
  );

  const tabs = buildTabs([
    {
      label: 'accommodations.marketing.sections.general',
      component: getTabComponent(General, {}),
      icon: <FileIcon />,
    },
    {
      label: 'accommodations.creation.sections.translations',
      component: getTabComponent(
        Translations,
        { warningMessage: t('accommodations.creation.englishRequired'), productType: 'accommodations' },
      ),
      icon: <FileIcon />,
    },
    {
      label: 'accommodations.marketing.sections.features',
      component: getTabComponent(Features, {}),
      icon: <ToolsIcon />,
    },
    {
      label: 'accommodations.marketing.sections.medias',
      component: getTabComponent(Medias, {}),
      icon: <PictureIcon />,
    },
  ]);

  return (
    <PageWrapper>
      <LayoutWithTabs
        tabs={tabs}
        breadcrumb={[
          { label: t('accommodations.title'), href: '/products/accommodations' },
        ]}
        title={t('accommodations.add')}
        skeletonLoader={(
          <>
            <SkeletonLoader className="m-b-30" height={30} />

            {tabs?.map((_, index) => (
              <React.Fragment key={index}>
                <SkeletonLoader className="m-b-20" height={50} />
                <SkeletonLoader className="m-b-50" height={200} />
              </React.Fragment>
            ))}
          </>
        )}
        moreContent={(
          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPostAccommodationLoading}
              disabled={!formik.dirty}
            >
              {t('accommodations.creation.form.submit')}
            </Button>
          </div>
        )}
      />
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(AccommodationCreation, { withTabsBar: true }),
    {
      scopes: [SCOPE.create_ownEst_extra],
    },
  ),
);
